import React, { useState, useEffect, ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import * as yup from "yup";
import { FetchPlanDetailAction } from "../../../../Redux/Actions/PaymentAction";

export interface Feature {
  feature: string;
  is_included: boolean;
  is_unlimited_usage: boolean;
}

export interface PaymentPlan {
  name: string;
  type: "USER" | "ORGANIZATION";
  credits: number;
  duration_type: "MONTHLY" | "QUARTERLY" | "ANNUAL" | "LIFETIME";
  features: Feature[];
  credit_rollover: boolean;
  price: number | undefined;
  description: string;
  is_unlimited: boolean;
  duration: number;
  category: "FREE" | "BASIC" | "PLUS" | "PRO";
}

interface UpdatePlanProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: any, params: string) => void;
  planId: number;
}

const schema = yup.object().shape({
  name: yup.string().required("Plan name is required"),
  type: yup.string().oneOf(["USER", "ORGANIZATION"]).required(),
  credits: yup.number().min(0).required(),
  duration_type: yup
    .string()
    .oneOf(["MONTHLY", "QUARTERLY", "ANNUAL", "LIFETIME"])
    .required(),
  price: yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string" && originalValue.trim() === "") {
        return undefined;
      }
      return value;
    })
    .min(0, "Price must be at least 0")
    .test(
      "two-decimals",
      "Price must have at most 2 decimal places",
      (value) => value !== undefined && Math.round(value * 100) === value * 100
    )
    .required("Plan price is required"),
  description: yup.string().required(),
  duration: yup.number().when("duration_type", {
    is: "LIFETIME",
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Duration is required"),
  }),
  category: yup.string().oneOf(["FREE", "BASIC", "PLUS", "PRO"]).required(),
  credit_rollover: yup.boolean(),
  is_unlimited: yup.boolean(),
  features: yup.array().of(
    yup.object().shape({
      feature: yup.string().required("Feature is required"),
      is_included: yup.boolean(),
      is_unlimited_usage: yup.boolean(),
    })
  ),
});

const UpdatePlan: React.FC<UpdatePlanProps> = ({
  open,
  onClose,
  onSubmit,
  planId,
}) => {
  const dispatch = useDispatch<any>();
  const planData = useSelector((state: any) => state.GetPlanDetailData.data);

  const initial: PaymentPlan = {
    name: "",
    type: "USER",
    credits: 0,
    duration_type: "MONTHLY",
    features: [],
    credit_rollover: false,
    price: 0,
    description: "",
    is_unlimited: false,
    duration: 0,
    category: "FREE",
  };

  const [form, setForm] = useState<PaymentPlan>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [priceInput, setPriceInput] = useState<string>("0.00");

  useEffect(() => {
    if (open && planId) {
      let params = `${planId}/`;
      dispatch(FetchPlanDetailAction(params));
    }
  }, [open, planId, dispatch]);

  useEffect(() => {
    if (planData) {
      setForm({
        name: planData.name,
        type: planData.type,
        credits: planData.credits,
        duration_type: planData.duration_type,
        features: planData.features,
        credit_rollover: planData.credit_rollover,
        price: planData.price,
        description: planData.description,
        is_unlimited: planData.is_unlimited,
        duration: planData.duration,
        category: planData.category,
      });
      setPriceInput(Number(planData.price).toFixed(2));
    }
  }, [planData]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (!name) return;
    let newVal: any = value;
    if (["credits", "duration"].includes(name)) {
      newVal = Number(value);
    } else if (type === "checkbox") {
      newVal = (e.target as HTMLInputElement).checked;
    }
    setForm((prev) => ({ ...prev, [name]: newVal }));
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPriceInput(e.target.value);
  };

  const handlePriceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.trim() === "") {
      setPriceInput("");
      setForm((prev) => ({ ...prev, price: undefined }));
    } else {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        const formatted = num.toFixed(2);
        setPriceInput(formatted);
        setForm((prev) => ({ ...prev, price: num }));
      } else {
        setPriceInput("");
        setForm((prev) => ({ ...prev, price: undefined }));
      }
    }
  };

  const addFeature = () => {
    setForm((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        { feature: "", is_included: false, is_unlimited_usage: false },
      ],
    }));
  };

  const removeFeature = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== idx),
    }));
  };

  const updateFeature = (idx: number, key: keyof Feature, val: any) => {
    const featuresCopy = [...form.features];
    featuresCopy[idx] = { ...featuresCopy[idx], [key]: val };
    setForm((prev) => ({ ...prev, features: featuresCopy }));
  };

  const handleSubmit = () => {
    schema
      .validate(form, { abortEarly: false })
      .then(() => {
        let params = `${planId}/`;
        const payload = {
          ...form,
          price: form.price !== undefined ? Number(form.price).toFixed(2) : "",
        };
        onSubmit(payload, params);
      })
      .catch((err: yup.ValidationError) => {
        const e: Record<string, string> = {};
        if (Array.isArray(err.inner) && err.inner.length > 0) {
          err.inner.forEach((errItem) => {
            if (errItem.path) e[errItem.path] = errItem.message;
          });
        } else if (err.path) {
          e[err.path] = err.message;
        }
        setErrors(e);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Update Plan</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="name"
            label="Plan Name"
            value={form.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            fullWidth
          />
          <FormControl fullWidth error={Boolean(errors.type)}>
            <InputLabel>Plan Type</InputLabel>
            <Select
              name="type"
              value={form.type}
              onChange={handleChange}
              label="Plan Type"
            >
              <MenuItem value="USER">User</MenuItem>
              <MenuItem value="ORGANIZATION">Organization</MenuItem>
            </Select>
          </FormControl>
          <TextField
            name="credits"
            label="Credits"
            type="number"
            value={form.credits}
            onChange={handleChange}
            error={Boolean(errors.credits)}
            helperText={errors.credits}
            InputProps={{ inputProps: { min: 0 } }}
            fullWidth
          />
          <FormControl fullWidth error={Boolean(errors.duration_type)}>
            <InputLabel>Duration Type</InputLabel>
            <Select
              name="duration_type"
              value={form.duration_type}
              onChange={handleChange}
              label="Duration Type"
            >
              <MenuItem value="MONTHLY">Monthly</MenuItem>
              <MenuItem value="QUARTERLY">Quarterly</MenuItem>
              <MenuItem value="ANNUAL">Annual</MenuItem>
              <MenuItem value="LIFETIME">Lifetime</MenuItem>
            </Select>
          </FormControl>
          <TextField
            name="price"
            label="Price"
            type="text"
            value={priceInput}
            onChange={handlePriceChange}
            onBlur={handlePriceBlur}
            error={Boolean(errors.price)}
            helperText={errors.price}
            fullWidth
          />
          <TextField
            name="description"
            label="Description"
            value={form.description}
            onChange={handleChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            name="duration"
            label="Duration"
            type="number"
            value={form.duration}
            onChange={handleChange}
            error={Boolean(errors.duration)}
            helperText={errors.duration}
            InputProps={{ inputProps: { min: 0 } }}
            fullWidth
          />
          <FormControl fullWidth error={Boolean(errors.category)}>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={form.category}
              onChange={handleChange}
              label="Category"
            >
              <MenuItem value="FREE">Free</MenuItem>
              <MenuItem value="BASIC">Basic</MenuItem>
              <MenuItem value="PLUS">Plus</MenuItem>
              <MenuItem value="PRO">Pro</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={form.credit_rollover}
                onChange={handleChange}
                name="credit_rollover"
              />
            }
            label="Credit Rollover"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_unlimited}
                onChange={handleChange}
                name="is_unlimited"
              />
            }
            label="Unlimited"
          />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <span style={{ fontWeight: "bold", marginRight: 8 }}>
                Features
              </span>
              <IconButton onClick={addFeature}>
                <AddIcon />
              </IconButton>
            </Box>
            {form.features.map((feat, idx) => (
              <Box
                key={idx}
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
              >
                <TextField
                  value={feat.feature}
                  onChange={(e) =>
                    updateFeature(idx, "feature", e.target.value)
                  }
                  error={Boolean(errors[`features.${idx}.feature`])}
                  helperText={errors[`features.${idx}.feature`]}
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={feat.is_included}
                      onChange={(e) =>
                        updateFeature(idx, "is_included", e.target.checked)
                      }
                    />
                  }
                  label="Included"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={feat.is_unlimited_usage}
                      onChange={(e) =>
                        updateFeature(
                          idx,
                          "is_unlimited_usage",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Unlimited Usage"
                />
                <IconButton onClick={() => removeFeature(idx)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Update Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdatePlan;
